import { useMemo, useState } from 'react'
import { Card, Flex, Grid, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { AccountPanel } from '@/features/profile/AccountPanel'
import { CommissionRulePanel } from '@/features/profile/CommissionRulePanel'
import { DayRangeBar } from '@/features/profile/DayRangeBar'
import { FundLimitsPanel } from '@/features/profile/FundLimitsPanel'
import { SummaryCards } from '@/features/profile/SummaryCards'
import {
  isQuickRange,
  lastIncludedInstant,
  quickRange,
  tenantRangeWindow,
  type DayRange,
} from '@/shared/lib/tenantDay'
import {
  useCommissionRule,
  useCommissionSummary,
  useFundLimits,
  useMyAccount,
} from '@/features/profile/useMyAccount'
import { formatDate } from '@/shared/lib/format'

/**
 * Ekranning eng katta kengligi.
 *
 * ⚠️ 560 dan kengaytirildi (egasi talabi, 2026-08-20). Sonlar
 * BIR-BIRIDAN UZOQLASHMAYDI, chunki bloklar ikki ustunga bo'linadi
 * (`COLUMN_BASIS`), ya'ni har ustunning o'zi ~440px bo'lib qoladi —
 * eski tartibdagidek. Kompyuterda esa endi to'rt blokning to'rttasi
 * ham bir ekranda ko'rinadi va sherik pastga aylantirmaydi.
 *
 * ⚠️ TELEFONGA TA'SIRI YO'Q: 390–430px li oynada bu chegara ham,
 * ikki ustun ham ishlamaydi — `flex-wrap` bloklarni bittalab
 * qatorlarga tushiradi va tartib bugungidek qoladi.
 */
const DESKTOP_MAX_WIDTH = 920

/**
 * Ustunning eng kichik kengligi.
 *
 * ⚠️ Bu son `wrap` ning ochqichi: undan tor joyda ikkinchi ustun
 * o'zidan pastga tushadi. Media so'rovi ATAYLAB yo'q — chegara
 * KONTENTdan kelib chiqadi, qurilma nomidan emas.
 */
const COLUMN_BASIS = 400

/**
 * «Mening hisobim» — 11-SHERIK-KABINETI-UI.md §6 va egasining
 * 2026-08-19 dagi qarori («sherikka to'liq moliyaviy ko'rinish»).
 *
 * Ekran to'rtta savolga javob beradi va boshqa hech nimaga:
 *   1. hisobim qanday turibdi (rejimga qarab `available` yoki
 *      `outstanding`, qarz limiti bilan) — `AccountPanel`;
 *   2. MEN NECHA FOIZDA ISHLAYAPMAN — `CommissionRulePanel` (egasi
 *      savoli, 2026-08-20). Bu blok yig'indidan OLDIN turadi: pastdagi
 *      cashback summasi aynan shu shartdan chiqadi va usiz uni
 *      tekshirib bo'lmaydi;
 *   3. bu oyda qancha to'ladim, qanchasi CASHBACK bo'lib qaytdi va sof
 *      qancha ketdi — `SummaryCards` (uch AJRATILGAN son);
 *   4. kunlik va oylik chegaramdan yana qancha qoldi — `FundLimitsPanel`.
 *
 * ⚠️ «DEPOZIT TO'LDIRISH» TUGMASI YO'Q va qo'shilmaydi: ADR-029 §2 —
 * `agent.deposit.add` sherikda YO'Q, aks holda agent o'ziga pul yozib
 * olardi. Tugma qo'yilsa u har bosishda 403 berardi.
 *
 * ⚠️ Depozit qoldig'ining O'ZI kassa ekranida ham bor
 * (`CashierBalanceBar`) va u yerda QOLADI: kassir pul berishdan OLDIN
 * qancha qolganini ko'rishi shart. Bu sahifa uni takrorlamaydi, boshqa
 * savolga javob beradi — rejim, limit, komissiya.
 *
 * ⚠️ Komissiya davri SERVERDAN so'raladi (davrsiz chaqiruv = joriy oy,
 * tenant zonasida). Oy chegarasini panelda hisoblash servernikidan yarim
 * soatga farq qilishi mumkin edi va bu JIMGINA boshqa son bo'lardi.
 *
 * <h2>⚠️ DAVR TANLANADIGAN BO'LDI — egasi talabi (2026-08-24)</h2>
 *
 * > «balans menyusida o'ng cardda bu oyda to'ldirishlar soni, summa,
 * > foyda ko'rsatilgan, lekin qolgan davrda ko'ra olmaydi. Default
 * > joriy oy bo'lsin lekin davrni tanlay olsin.»
 *
 * ⚠️ SERVER TOMON TEKSHIRILDI, taxmin qilinmadi:
 * `AgentMoneyController.commissionSummary` ikkala parametrni ham
 * OLADI — `@RequestParam(required = false) Instant from` va `to`,
 * `agent.read` ostida; berilmasa `CommissionServiceImpl.summary`
 * `TenantCalendar.currentMonthRange()` ga tushadi. Ya'ni panel soxta
 * filtr yasamayapti, mavjud imkoniyatni ulayapti.
 *
 * ⛔ SUKUT HOLATDA PARAMETR YUBORILMAYDI ({@link period} `null`) va bu
 * yuqoridagi qarorning DAVOMI, uning bekor qilinishi emas: «joriy oy»
 * ni panelda hisoblash Kobul tenantida (UTC+04:30) servernikidan yarim
 * soatga farq qilishi mumkin va oy chegarasida u JIMGINA boshqa son
 * berardi. Shuning uchun «Shu oy» tugmasi ham davrni tozalaydi —
 * hisoblangan oyni yubormaydi.
 */
export function ProfilePage() {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false
  const account = useMyAccount()

  /**
   * Tanlangan davr; `null` — DAVRSIZ so'rov, ya'ni serverning joriy oyi.
   *
   * ⚠️ `null` va «hisoblangan joriy oy» BIR XIL EMAS — yuqoridagi izoh.
   */
  const [period, setPeriod] = useState<DayRange | null>(null)

  // ⚠️ Tanlagichda ko'rsatiladigan qiymat. `null` holatda u panelda
  // hisoblangan joriy oyni CHIZADI (tanlagich bo'sh tura olmaydi),
  // lekin SO'ROVGA tushmaydi — chizish bilan so'rash bu yerda ataylab
  // ajratilgan.
  const shownRange = useMemo(() => period ?? quickRange('month'), [period])

  // ⚠️ `useMemo` MAJBURIY: `tenantRangeWindow` har chaqirishda YANGI
  // obyekt qaytaradi va u to'g'ridan-to'g'ri hookka berilsa
  // `queryKey` har render'da o'zgarardi — cheksiz qayta so'rov.
  const window = useMemo(() => (period === null ? null : tenantRangeWindow(period)), [period])

  const commission = useCommissionSummary(account.agentId, window)
  const commissionRule = useCommissionRule(account.agentId)
  const fundLimits = useFundLimits(account)

  const column = { flex: `1 1 ${COLUMN_BASIS}px`, minWidth: 0 }

  return (
    <div style={{ maxWidth: isMobile ? undefined : DESKTOP_MAX_WIDTH, margin: '0 auto' }}>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 2 }}>
        {t('profile.title')}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t('profile.subtitle')}</Typography.Paragraph>

      {/* ⚠️ MANBA TARTIBI SAQLANADI: qoida yig'indidan OLDIN turadi.
          Ikki ustunda ular yonma-yon tushadi, ya'ni cashback summasi
          va uni tushuntiruvchi shart bir vaqtda ko'rinadi — tor
          ekranda esa `wrap` ularni yana o'sha tartibda ustma-ust
          qo'yadi. */}
      <Flex gap={16} wrap align="flex-start">
        <Flex vertical gap={16} style={column}>
          <AccountPanel account={account} />

          {account.agentId !== null ? (
            <Card size="small" title={t('profile.ruleTitle')}>
              <CommissionRulePanel state={commissionRule} />
            </Card>
          ) : null}
        </Flex>

        {account.agentId !== null ? (
          <Flex vertical gap={16} style={column}>
            {/* ⚠️ SARLAVHA DAVRGA QARAB O'ZGARADI. «Bu oyda» deb turgan
                sarlavha ostida boshqa davrning soni turishi eng yomon
                variant bo'lardi: son ishonarli, sarlavha yolg'on va
                buni hech kim tekshirmasdi. */}
            <Card
              size="small"
              title={period === null ? t('profile.commissionMonth') : t('profile.commissionPeriod')}
            >
              <Flex vertical gap={12}>
                {/* ⚠️ AYNI TANLAGICH «Mening amallarim» va «Berilgan
                    paketlar» da ham ishlatiladi — kassir uni allaqachon
                    biladi va u kun chegarasini TENANT zonasida hisoblaydi
                    (`tenantDay.ts`). Bu yerga ikkinchi nusxa yozilsa,
                    Kobul tenantidagi yarim soatlik siljish faqat shu
                    ekranda eskirardi.

                    ⛔ «Shu oy» tugmasi davrni TOZALAYDI (`null`), o'zi
                    hisoblagan oyni YUBORMAYDI — sahifa izohidagi sabab.
                    Qolgan tugmalar va kalendar odatdagidek ishlaydi. */}
                <DayRangeBar
                  range={shownRange}
                  onChange={(next) => setPeriod(isQuickRange(next, 'month') ? null : next)}
                />

                <SummaryCards state={commission} awaitingNote={t('profile.commissionAwaiting')} />

                {/* ⚠️ SONLAR QAYSI DAVRGA TEGISHLI EKANI SERVERDAN
                    o'qiladi (`CommissionSummary.from`/`to`), panelning
                    tanlovidan emas: server davrni tenant zonasida o'zi
                    yakunlaydi va davrsiz so'rovda uni PANEL umuman
                    bilmaydi. `to` KIRITILMAYDIGAN chegara, shuning uchun
                    ekranda undan bir kun oldingi sana ko'rsatiladi.  */}
                {commission.summary ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('profile.commissionRange', {
                      from: formatDate(commission.summary.from),
                      to: formatDate(lastIncludedInstant(commission.summary.to)),
                    })}
                  </Typography.Text>
                ) : null}
              </Flex>
            </Card>

            <Card size="small" title={t('profile.limitsTitle')}>
              <FundLimitsPanel state={fundLimits} />
            </Card>
          </Flex>
        ) : null}
      </Flex>
    </div>
  )
}
